import React from "react";
import { Slider } from "./ui/slider";
import { Play, Pause, SkipBack, SkipForward, Volume2, X } from "lucide-react";
import { usePlayer, formatTime } from "../context/PlayerContext";

export const AudioPlayer = () => {
    const {
        current,
        isPlaying,
        progress,
        duration,
        volume,
        togglePlay,
        seek,
        skip,
        setVolume,
    } = usePlayer();

    if (!current) return null;

    return (
        <div
            data-testid="audio-player"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[min(960px,calc(100vw-2rem))] glass player-glow rounded-full pl-3 pr-3 py-2.5 flex items-center gap-3"
        >
            <img
                src={current.cover_url}
                alt=""
                className="w-12 h-12 rounded-full object-cover hidden sm:block border border-border"
            />
            <div className="hidden md:block min-w-0 max-w-[180px]">
                <div className="text-xs text-lilac-700 eyebrow truncate">EP {String(current.number).padStart(2, "0")}</div>
                <div className="text-sm text-lilac-900 font-medium truncate">{current.title}</div>
            </div>

            <div className="flex items-center gap-1">
                <button
                    data-testid="player-skip-back"
                    onClick={() => skip(-15)}
                    className="grid place-items-center w-9 h-9 rounded-full hover:bg-lilac-100 text-lilac-900"
                    aria-label="Back 15s"
                >
                    <SkipBack className="w-4 h-4" />
                </button>
                <button
                    data-testid="player-toggle"
                    onClick={togglePlay}
                    className="grid place-items-center w-11 h-11 rounded-full bg-gold hover:bg-gold-deep text-white shadow-[0_8px_24px_rgba(212,175,55,0.4)]"
                    aria-label={isPlaying ? "Pause" : "Play"}
                >
                    {isPlaying ? (
                        <Pause className="w-4 h-4" fill="currentColor" />
                    ) : (
                        <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                    )}
                </button>
                <button
                    data-testid="player-skip-forward"
                    onClick={() => skip(15)}
                    className="grid place-items-center w-9 h-9 rounded-full hover:bg-lilac-100 text-lilac-900"
                    aria-label="Forward 15s"
                >
                    <SkipForward className="w-4 h-4" />
                </button>
            </div>

            <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-[11px] tabular-nums text-lilac-700 hidden sm:inline">
                    {formatTime(progress)}
                </span>
                <Slider
                    data-testid="player-scrub"
                    value={[progress]}
                    max={Math.max(duration, 0.0001)}
                    step={0.5}
                    onValueChange={(v) => seek(v[0])}
                    className="flex-1"
                />
                <span className="text-[11px] tabular-nums text-lilac-700 hidden sm:inline">
                    {formatTime(duration)}
                </span>
            </div>

            <div className="hidden md:flex items-center gap-2 w-28">
                <Volume2 className="w-4 h-4 text-lilac-700" />
                <Slider
                    data-testid="player-volume"
                    value={[volume * 100]}
                    max={100}
                    step={1}
                    onValueChange={(v) => setVolume(v[0] / 100)}
                />
            </div>
        </div>
    );
};
